import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { firstValueFrom } from 'rxjs';

export const adminGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // First check if user is authenticated
  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  // Check if user has ROLE_ADMIN using the same logic as HasAnyRoleDirective
  const requiredRoles = ['ROLE_ADMIN'];
  
  // Get current user from AuthService
  const currentUser = await firstValueFrom(authService.currentUser$);

  if (checkRoles(requiredRoles, currentUser)) {
    return true;
  }

  // User is authenticated but doesn't have admin role
  // Redirect to home page or show access denied
  router.navigate(['/']);
  return false;
};

function checkRoles(requiredRoles: string[], currentUser: any): boolean {
  if (!requiredRoles || requiredRoles.length === 0) {
    return false;
  }

  // Check localStorage 'role' (array of role strings)
  const roleStr = localStorage.getItem('role');
  if (roleStr) {
    try {
      const roles = JSON.parse(roleStr);
      if (Array.isArray(roles)) {
        const hasAny = requiredRoles.some(requiredRole => roles.includes(requiredRole));
        if (hasAny) return true;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Check localStorage 'jhi-cn_role' (array of role objects with role property)
  const cnRoleStr = localStorage.getItem('jhi-cn_role');
  if (cnRoleStr) {
    try {
      const cnRoles = JSON.parse(cnRoleStr);
      if (Array.isArray(cnRoles)) {
        const hasAny = requiredRoles.some(requiredRole => 
          cnRoles.some((roleObj: any) => roleObj?.role === requiredRole || roleObj?.role?.includes(requiredRole))
        );
        if (hasAny) return true;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Check currentUser from AuthService
  if (currentUser) {
    // Check roles array
    if (currentUser.roles && Array.isArray(currentUser.roles)) {
      const hasAny = requiredRoles.some(requiredRole => 
        currentUser.roles.some((role: any) => {
          if (typeof role === 'string') {
            return role === requiredRole || role.includes(requiredRole);
          }
          // Support both 'role' and 'name' properties for role objects
          const roleValue = role?.role || role?.name;
          return roleValue === requiredRole || roleValue?.includes(requiredRole);
        })
      );
      if (hasAny) return true;
    }

    // Check global_roles array
    if (currentUser.global_roles && Array.isArray(currentUser.global_roles)) {
      const hasAny = requiredRoles.some(requiredRole => 
        currentUser.global_roles.some((role: any) => {
          if (typeof role === 'string') {
            return role === requiredRole || role.includes(requiredRole);
          }
          // Support both 'role' and 'name' properties for role objects
          const roleValue = role?.role || role?.name;
          return roleValue === requiredRole || roleValue?.includes(requiredRole);
        })
      );
      if (hasAny) return true;
    }
  }

  return false;
}

